(ns app.agenda.home
  "Shared components for the schedule pages: onboarding flow, IG user
   selector, story cards, status badges, and helpers."
  (:require [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [app.bundle-social.platforms :as social-platforms]
            [app.client-log :as client-log]
            [ui.app-frame :as app-frame]
            [ui.account-selector :as account-selector]
            [ui.navbar :as navbar]
            [ui.sidebar :as sidebar]
            [ui.gallery.link :as gallery-link]
            [storrito.calendly.links :as calendly]
            [storrito.instagram.proxy-error :as proxy-error]
            [storrito.util.link :as link]
            [storrito.date.format :as f]
            [cljs-time.core :as t]
            [cljs-time.coerce :as tc]
            [lambdaisland.uri :as uri]
            [ui.animation-editor.model :as animation-editor]))

;; -- Queries -----------------------------------------------------------------

(defn- instagram-account-key
  [id]
  (str "instagram:" id))

(defn- agenda-start-date
  "Returns the selected agenda start date or today."
  [state]
  (or (::agenda-date state)
      (tc/to-date (t/today-at-midnight))))

(defn home-query
  "Query for the schedule pages. Includes the currently selected filters
   so notify refresh can re-run the query directly from `:ui.query/log`."
  [state]
  (let [selected-account-key (::selected-account-key state)
        selected-id (::selected-ig-user-id state)]
    (cond-> {:query/kind :query/home
             :query/refresh? true
             :agenda/start-date (.getTime (agenda-start-date state))}
      selected-account-key
      (assoc :schedule/account-key selected-account-key)

      (and (not selected-account-key) selected-id)
      (assoc :instagram.user/id selected-id))))

(def user-session-query
  {:query/kind :query/user-session})

(def trial-banner-href
  "The trial banner's link: the wallet page with the $5 chip preselected
   (ui.wallet.page/amount-from-location) and the banner as the source."
  "/ui/subscription?source=trial-banner&amount=5")

(def trial-banner-cta-label
  "The label of the trial banner's link. It names no amount although the
   link preselects $5: as \"Add $5 credit\" (28.–30.09.2026) the link was
   clicked on 1 of 53 days an organization saw the banner, as \"Add
   credits\" on 17 of 172 before - presumably a price on the button
   reads like the payment itself; the amount belongs to the wallet page."
  "Add credits")

(def add-social-account-action
  [[:ui/modal-open :modals/social-account-connect {:source "agenda"}]])

(def onboarding-add-social-account-action
  [[:ui/modal-open :modals/social-account-connect {:skip-instagram-info? true
                                                   :source "welcome-screen"}]])

(def create-screen-add-social-account-action
  "The connect button of the create screen, shown after \"Skip for now\"
   on the welcome screen. The source lands in the connect3 URL."
  [[:ui/modal-open :modals/social-account-connect {:skip-instagram-info? true
                                                   :source "create-screen"}]])

;; -- Onboarding --------------------------------------------------------------

(defn- skip-button
  "Optionally logs `log-event` as a funnel signal when the skip is used."
  ([action-key]
   (skip-button action-key nil))
  ([action-key log-event]
   [:a
    {:class ["text-gray-400" "hover:text-gray-600" "text-sm"
             "cursor-pointer" "transition-colors" "duration-200"]
     :on {:click (cond-> [[:store/assoc action-key true]]
                   log-event (conj [:client-log/log log-event]))}}
    "Skip for now"]))

(defn- support-link [text]
  [:p
   {:class ["text-sm" "text-gray-500" "mt-8"]}
   text " "
   [:a
    {:href (link/help-center-url "7a86df51-8f0f-4295-b0d9-5ca80ad8106a")
     :target "_blank"
     :class ["text-storrito-500" "hover:text-storrito-600" "underline"
             "underline-offset-2"]}
    "help center"]
   " or "
   [:a
    {:class ["text-storrito-500" "hover:text-storrito-600" "underline"
             "underline-offset-2" "cursor-pointer"]
     :on {:click [[:crisp/open-chat]]}}
    "contact support"]])

(defn- support-and-call-link [session]
  [:p
   {:class ["text-sm" "text-gray-500" "mt-8"]}
   "Need help? Visit our "
   [:a
    {:href (link/help-center-url "7a86df51-8f0f-4295-b0d9-5ca80ad8106a")
     :target "_blank"
     :class ["text-storrito-500" "hover:text-storrito-600" "underline"
             "underline-offset-2"]}
    "help center"]
   ", "
   [:a
    {:class ["text-storrito-500" "hover:text-storrito-600" "underline"
             "underline-offset-2" "cursor-pointer"]
     :on {:click [[:crisp/open-chat]]}}
    "contact support"]
   " or "
   [:a
    {:href (calendly/url :connect-help "welcome-screen" (:organization/uuid session))
     :target "_blank"
     :class ["text-storrito-500" "hover:text-storrito-600" "underline"
             "underline-offset-2"]}
    "book a call to get started!"]])

(defn- onboarding-shell
  "Full-screen onboarding layout with deep navy background."
  [& children]
  (into
    [:div
     {:class ["flex" "flex-col" "items-center" "justify-center"
              "min-h-full" "bg-gradient-to-br" "from-storrito-50"
              "to-white" "p-4" "sm:p-8" "w-full"]}
     [:div
      {:class ["w-full" "max-w-lg" "mx-auto"]}]]
    children))

(defn- onboarding-sidebar-hidden?
  [state]
  (get state ::hide-side-menu? true))

(defn- onboarding-menu-icon
  [hidden?]
  [:svg
   {:xmlns "http://www.w3.org/2000/svg"
    :viewBox "0 0 20 20"
    :fill "currentColor"
    :aria-hidden "true"
    :class ["h-5" "w-5"]}
   (if hidden?
     [:path
      {:fill-rule "evenodd"
       :clip-rule "evenodd"
       :d "M2 4.75A.75.75 0 0 1 2.75 4h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 4.75ZM2 10a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 10Zm0 5.25a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1-.75-.75Z"}]
     [:path
      {:fill-rule "evenodd"
       :clip-rule "evenodd"
       :d "M4.293 4.293a1 1 0 0 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414L11.414 10l4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L8.586 10 4.293 5.707a1 1 0 0 1 0-1.414Z"}])])

(defn- onboarding-sidebar-toggle
  [state]
  (let [hidden? (onboarding-sidebar-hidden? state)]
    [:button
     {:type "button"
      :aria-label (if hidden? "Open sidebar" "Close sidebar")
      :style {:left (if hidden?
                      "1rem"
                      "min(15.75rem, calc(100vw - 3.5rem))")}
      :class ["fixed" "top-4" "z-50" "inline-flex" "h-10" "w-10"
              "items-center" "justify-center" "rounded-full" "bg-white"
              "text-gray-700" "shadow-lg" "ring-1" "ring-gray-200"
              "hover:bg-gray-50" "hover:text-gray-900" "cursor-pointer"]
      :on {:click [[:store/assoc ::hide-side-menu? (not hidden?)]]}}
     (onboarding-menu-icon hidden?)]))

(defn- onboarding-user-menu-button
  [state]
  [:div.fixed.right-4.top-4.z-50
   (navbar/user-menu-button
     state
     {:class ["h-10" "w-10" "justify-center" "rounded-full" "bg-white"
              "text-gray-700" "shadow-lg" "ring-1" "ring-gray-200"
              "hover:bg-gray-50" "hover:text-gray-900"
              "sm:h-10" "sm:w-auto" "sm:justify-start" "sm:px-4"
              "sm:py-2" "sm:text-sm" "sm:font-medium"
              "sm:whitespace-nowrap"]
      :email-class ["hidden" "sm:inline" "sm:whitespace-nowrap"]
      :icon-margin-class ["sm:ml-3"]})])

(defn- onboarding-frame
  "Minimal app navigation chrome for setup screens that are shown before
   onboarding is completed."
  [state content]
  (let [hidden? (onboarding-sidebar-hidden? state)]
    [:div.flex.h-full.relative
     (onboarding-sidebar-toggle state)
     (when-not hidden?
       (sidebar/sidebar state))
     (onboarding-user-menu-button state)
     (navbar/logout-menu state)
     [:div.relative.grow.overflow-y-auto
      content]]))

(defn- step-indicator
  "Visual step dots. current is 1-indexed."
  [current total]
  [:div
   {:class ["flex" "items-center" "justify-center" "gap-2" "mb-8"]}
   (for [i (range 1 (inc total))]
     (let [[w h bg] (cond
                      (= i current) ["w-8" "h-2" "bg-storrito-500"]
                      (< i current) ["w-2" "h-2" "bg-storrito-400"]
                      :else          ["w-2" "h-2" "bg-gray-300"])]
       [:div
        {:class ["rounded-full" "transition-all" "duration-300" w h bg]}]))])

(defn- subscribed?
  [session]
  (or (:subscription-id session)
      (:organization/legacy-subscription-id session)
      (:organization/legacy-customer? session)))

(defn show-subscribe-screen?
  "True when the onboarding subscribe screen should replace the schedule
   view: the trial is over and the user has seen the product work.

   `:organization/free-account?` means \"never received any credits\", so
   it is false for start-credit tenants — they need their own condition:
   first story posted and trial credits used up. The legacy condition
   covers old free accounts that used their 3 free posts."
  [session subscribe-skipped?]
  (boolean
    (and (not (subscribed? session))
         (not (:subscription-canceled session))
         (not subscribe-skipped?)
         (or (and (:free-plan.trial/start-credit-amount session)
                  (:organization/story-posted? session)
                  (not (:organization/positive-credit-balance? session)))
             (and (:organization/free-account? session)
                  (not (pos? (:organization/trial-posts-remaining session))))))))

(defn trial-banner-headline
  "How long the free credit still lasts, for the trial banner."
  [amount-formatted days-left]
  (let [credit (str "Your "
                    (when amount-formatted
                      (str amount-formatted " "))
                    "free credit")]
    (cond
      (nil? days-left) (str credit " is running low")
      (zero? days-left) (str credit " runs out today")
      (= 1 days-left) (str credit " lasts about 1 more day")
      :else (str credit " lasts about " days-left " more days"))))

(defn connected-account?
  "True when at least one Instagram account of the home query is
   connected, i.e. the daily connection fee is running."
  [home-result]
  (boolean (some (complement :instagram.account/not-connected)
                 (:instagram/users home-result))))

(defn show-trial-banner?
  "The pay-as-you-go nudge on the schedule, from the first connected
   account or posted story until the start credit is used up; then
   `show-subscribe-screen?` takes over the whole page. It used to wait
   for the first story: of 44 trial tenants who saw it in 16.–21.09.2026,
   10 went to the checkout, while two thirds of the connected tenants
   never posted and so never saw it. `days-left` comes from the home
   query (`app.agenda.trial-runway`), nil for everybody but trial
   tenants."
  [session home-result dismissed?]
  (boolean
    (and (navbar/trial-user? session)
         (or (:organization/story-posted? session)
             (connected-account? home-result))
         (:organization/positive-credit-balance? session)
         (some? (:trial/days-left home-result))
         (not dismissed?))))

(defn- trial-banner
  [session days-left]
  (let [urgent? (<= days-left 1)]
    [:div
     {:class ["flex" "flex-col" "sm:flex-row" "sm:items-center" "gap-3"
              "p-4" "rounded-lg" "border"
              (if urgent? "bg-amber-50" "bg-green-50")
              (if urgent? "border-amber-200" "border-green-200")]
      ;; Funnel touchpoint, counted by the admin dashboard as "Trial
      ;; banner shown".
      :replicant/on-mount (fn [_]
                            (client-log/log! :onboarding/trial-banner
                                             {:trial/days-left days-left}))}
     [:div
      {:class ["flex-1" "min-w-0"]}
      [:p
       {:class ["text-sm" "font-semibold"
                (if urgent? "text-amber-800" "text-green-800")]}
       (trial-banner-headline
         (:free-plan.trial/start-credit-amount-formatted session)
         days-left)]
      [:p
       {:class ["text-sm" (if urgent? "text-amber-700" "text-green-700")]}
       (if (:organization/story-posted? session)
         "Stories only go live with a positive balance. Keep posting with pay-as-you-go credits from $5."
         "Your connected account uses credit every day. Stories only go live with a positive balance — pay-as-you-go credits from $5.")]]
     [:a
      ;; `amount=5` preselects the $5 chip on the wallet page (see
      ;; ui.wallet.page/amount-from-location): the banner is the best
      ;; converting entry, the amount choice on the page the step most
      ;; trial users never take.
      {:href trial-banner-href
       :class ["inline-flex" "items-center" "justify-center" "px-4" "py-2"
               "rounded-lg" "text-sm" "font-semibold" "text-white"
               "bg-storrito-500" "hover:bg-storrito-600"
               "transition-colors" "shrink-0"]}
      trial-banner-cta-label]
     [:button
      {:type "button"
       :aria-label "Dismiss"
       :class ["self-start" "sm:self-center" "text-gray-400"
               "hover:text-gray-600" "cursor-pointer" "shrink-0"]
       :on {:click [[:store/assoc ::trial-banner-dismissed true]]}}
      [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
             :fill "currentColor" :class ["w-5" "h-5"]}
       [:path {:d "M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"}]]]]))

(defn- free-badge [session]
  (when-not (subscribed? session)
    [:span
     {:class ["inline-flex" "items-center" "px-3" "py-1" "rounded-full"
              "text-xs" "font-bold" "tracking-wide" "uppercase"
              "bg-green-100" "text-green-700"]}
     (str "First "
          (or (:free-plan.trial/start-credit-amount-formatted session)
              "$1")
          " on us")]))

(defn- welcome-connect-screen [session]
  (onboarding-shell
    [:div
     ;; Funnel step: the first onboarding screen was actually shown
     ;; (in contrast to :app/loaded, which also fires for returning
     ;; users landing directly on the agenda). The key keeps Replicant
     ;; from reusing this node for the next onboarding screen, whose
     ;; on-mount would then never run.
     {:class ["text-center" "mb-8"]
      :replicant/key ::welcome-screen
      :replicant/on-mount (fn [_]
                            (client-log/log! :onboarding/welcome-screen))}
     [:img {:src "/img/logo/burrito.svg"
            :class ["h-12" "w-12" "mx-auto" "mb-6"]}]
     (step-indicator 1 3)
     [:h1
      {:class ["text-3xl" "sm:text-4xl" "font-bold" "text-gray-900" "mb-3"
               "tracking-tight"]}
      "Welcome to Storrito"]
     [:p
      {:class ["text-gray-600" "text-lg" "mb-4"]}
      "Schedule and post social content from your desktop."]
     (free-badge session)]
    [:div
     {:class ["bg-white" "rounded-2xl" "p-6" "sm:p-8" "mb-6"
              "shadow-2xl" "shadow-black/20"]}
     [:h2
      {:class ["text-xl" "font-semibold" "text-gray-800" "mb-2"]}
      "Connect your social account"]
     [:p
      {:class ["text-gray-500" "text-sm" "mb-6"]}
      "Choose Instagram, TikTok, or YouTube. You'll be able to schedule and post right away."]
     [:button
      {:class ["w-full" "py-3.5" "px-6" "rounded-xl"
               "text-white" "font-semibold" "text-base"
               "bg-storrito-500" "hover:bg-storrito-600"
               "transition-all" "duration-200"
               "shadow-lg" "shadow-storrito-500/25"
               "cursor-pointer"]
       :on {:click onboarding-add-social-account-action}}
      "Connect a social account"]
     [:div
      {:class ["mt-8" "grid" "grid-cols-3" "gap-3"]}
      (for [[num label] [[1 "Connect account"]
                          [2 "Create a story"]
                          [3 "Watch it post"]]]
        [:div {:class ["text-center"]}
         [:div {:class ["w-8" "h-8" "rounded-full" "bg-storrito-500"
                         "text-white" "text-sm" "font-bold"
                         "flex" "items-center" "justify-center"
                         "mx-auto" "mb-2"]}
          (str num)]
         [:p {:class ["text-xs" "text-gray-600" "leading-tight"]}
          label]])]
     (support-and-call-link session)]
    [:div
     {:class ["text-center" "space-y-3"]}
     [:a {:href "/ui/editor2"
          :class ["text-storrito-500" "hover:text-storrito-600" "text-sm"
                  "underline" "underline-offset-2" "transition-colors"]}
      "Explore the editor first"]
     [:div (skip-button ::connect-skipped :onboarding/connect-skipped)]]))

(defn- no-account-card
  "Takes the place of the account card on the create screen when the
   welcome screen's connect step was skipped: 9 % of the signups skip,
   and they connect at half the rate of the others (15 % vs 31 %) - the
   screen used to claim \"Your account is ready\" and offered no way to
   connect one."
  []
  [:div
   {:class ["flex" "items-center" "justify-between" "gap-3" "p-3" "rounded-xl"
            "bg-gray-50" "border" "border-gray-200"]}
   [:div
    [:p {:class ["text-sm" "font-semibold" "text-gray-800"]}
     "No social account yet"]
    [:p {:class ["text-xs" "text-gray-500" "mt-0.5"]}
     "Needed to post. Instagram, TikTok or YouTube."]]
   [:button
    {:class ["shrink-0" "py-2" "px-4" "rounded-lg" "text-sm" "font-semibold"
             "text-storrito-600" "bg-white" "border" "border-storrito-200"
             "hover:bg-storrito-50" "transition-colors" "duration-200"
             "cursor-pointer"]
     :on {:click create-screen-add-social-account-action}}
    "Connect"]])

(defn- ig-account-card
  "Account display with profile picture and username."
  [user]
  (let [username (or (:account/name user)
                     (:instagram.user/name user))
        pic-url (or (:account/profile-picture-url user)
                    (:instagram.user/profile-picture-url user))]
    [:div
     {:class ["flex" "items-center" "gap-3" "p-3" "rounded-xl"
              "bg-gradient-to-r" "from-purple-50" "via-pink-50" "to-orange-50"
              "border" "border-pink-100"]}
     [:div
      {:class ["w-12" "h-12" "rounded-full" "overflow-hidden" "shrink-0"
               "ring-2" "ring-pink-300" "ring-offset-2"
               "bg-gradient-to-br" "from-purple-400" "via-pink-500" "to-orange-400"]}
      (if pic-url
        [:img {:src pic-url
               :class ["w-full" "h-full" "object-cover"]}]
        [:div {:class ["w-full" "h-full" "flex" "items-center"
                       "justify-center" "text-white" "text-lg" "font-bold"]}
         (some-> username first str .toUpperCase)])]
     [:div
      [:p {:class ["text-sm" "font-semibold" "text-gray-800"]}
       (str "@" username)]
      [:div {:class ["flex" "items-center" "gap-1" "mt-0.5"]}
       [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 24 24"
              :fill "currentColor"
              :class ["w-3.5" "h-3.5" "text-green-500"]}
        [:path {:fill-rule "evenodd" :clip-rule "evenodd"
                :d "M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"}]]
       [:span {:class ["text-xs" "text-green-600"]}
        "Connected"]]]]))

(defn create-option-chosen-action
  "Logs which of the create screen's two ways to the first content was
   picked. The link itself navigates inside the SPA, the log is fire and
   forget."
  [option]
  [[:client-log/log :onboarding/create-option-chosen {:create/option option}]])

(defn- create-screen
  "`days-left` (see `app.agenda.trial-runway`) is given once an account
   is connected: the runway then replaces \"No credit card needed\" in
   the credit box, so the countdown starts where the credit is first
   consumed and not only on the schedule after the first story."
  [session users days-left]
  (let [user (first users)]
    (onboarding-shell
      [:div
       ;; Funnel step: account connected (or skipped), no content yet.
       ;; Keyed, because the screen usually replaces the welcome screen in
       ;; place (stale home query after the connect, or the skip link):
       ;; without a node of its own the on-mount does not run.
       {:class ["text-center" "mb-8"]
        :replicant/key ::create-screen
        :replicant/on-mount (fn [_]
                              (client-log/log! :onboarding/create-screen))}
       [:img {:src "/img/logo/party_emoji.svg"
              :class ["h-16" "w-16" "mx-auto" "mb-4"]}]
       (step-indicator 2 3)
       [:h1
        {:class ["text-3xl" "sm:text-4xl" "font-bold" "text-gray-900" "mb-3"
                 "tracking-tight"]}
        (if user "You're all set!" "Create your first story")]
       [:p
        {:class ["text-gray-600" "text-lg"]}
        (if user
          "Your account is ready. Create your first story and watch it post automatically."
          "Design it now, connect your social account when it is ready to post.")]]
      [:div
       {:class ["bg-white" "rounded-2xl" "p-6" "sm:p-8" "mb-6"
                "shadow-2xl" "shadow-black/20"]}
       [:div {:class ["mb-6"]}
        (if user
          (ig-account-card user)
          (no-account-card))]
       (when-let [badge (free-badge session)]
         [:div
          {:class ["flex" "flex-wrap" "items-center" "gap-2" "mb-6" "p-3" "rounded-lg"
                   "bg-green-50" "border" "border-green-100"]
           ;; Same funnel touchpoint as the schedule's trial banner
           ;; ("Trial banner shown" in the admin dashboard). Keyed once
           ;; the runway is known, so that a box rendered without it
           ;; before is not reused (its on-mount would not run).
           :replicant/key (when (some? days-left)
                            ::create-screen-trial-banner)
           :replicant/on-mount (when (some? days-left)
                                 (fn [_]
                                   (client-log/log! :onboarding/trial-banner
                                                    {:trial/days-left days-left})))}
          badge
          (if (some? days-left)
            [:span {:class ["text-sm" "text-green-800"]}
             (trial-banner-headline
               (:free-plan.trial/start-credit-amount-formatted session)
               days-left)
             " · "
             [:a {:href trial-banner-href
                  :class ["underline" "font-medium"]}
              trial-banner-cta-label]]
            [:span {:class ["text-sm" "text-gray-500"]}
             "No credit card needed"])])
       [:a
        {:href "/ui/editor2"
         :class ["block" "w-full" "py-3.5" "px-6" "rounded-xl" "text-center"
                 "text-white" "font-semibold" "text-base"
                 "bg-storrito-500" "hover:bg-storrito-600"
                 "transition-all" "duration-200"
                 "shadow-lg" "shadow-storrito-500/25"]
         :on {:click (create-option-chosen-action "editor")}}
        "Create your first story"]
       [:a
        {:href (gallery-link/gallery-href)
         :class ["block" "w-full" "py-3" "px-6" "mt-3" "rounded-xl" "text-center"
                 "text-gray-600" "font-medium" "text-sm"
                 "border" "border-gray-200" "hover:bg-gray-50"
                 "transition-colors" "duration-200"]
         :on {:click (create-option-chosen-action "upload")}}
        "Or upload existing content"]
       (support-link "Need inspiration? Visit our")])))

;; -- Helpers (public) --------------------------------------------------------

(defn post-platform
  [post]
  (or (:schedule/platform post)
      :instagram))

(defn tiktok-post?
  [post]
  (= :tiktok (post-platform post)))

(defn youtube-post?
  [post]
  (= :youtube (post-platform post)))

(defn reel-post?
  [post]
  (= :reel (post-platform post)))

(defn social-platform
  "The bundle.social platform of the post, nil for Storrito's own
   platforms."
  [post]
  (social-platforms/by-key (post-platform post)))

(defn- instagram-icon
  [classes]
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :viewBox "0 0 448 512"
         :fill "currentColor"
         :aria-hidden "true"
         :class classes}
   [:path {:d "M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"}]])

(defn- tiktok-icon
  [classes]
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :viewBox "0 0 448 512"
         :fill "currentColor"
         :aria-hidden "true"
         :class classes}
   [:path {:d "M448 209.9a210.1 210.1 0 01-122.8-39.3v178.8A162.6 162.6 0 11185 188.3v89.9a74.6 74.6 0 1052.2 71.2V0h88a121 121 0 00122.8 121.1z"}]])

(defn- youtube-icon
  [classes]
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :viewBox "0 0 576 512"
         :fill "currentColor"
         :aria-hidden "true"
         :class classes}
   [:path {:d "M549.7 124.1c-6.3-23.7-24.8-42.3-48.3-48.6C458.8 64 288 64 288 64S117.2 64 74.6 75.5c-23.5 6.3-42 24.9-48.3 48.6C15 167 15 256.4 15 256.4s0 89.4 11.3 132.3c6.3 23.7 24.8 41.5 48.3 47.8C117.2 448 288 448 288 448s170.8 0 213.4-11.5c23.5-6.3 42-24.2 48.3-47.8C561 345.8 561 256.4 561 256.4s0-89.4-11.3-132.3zM232 337.6V175.2l142.7 81.2L232 337.6z"}]])

(defn platform-icon
  [platform classes]
  (if-let [social-platform (social-platforms/by-key platform)]
    ((:social-platform/icon social-platform) classes)
    (case platform
      :tiktok (tiktok-icon classes)
      :youtube (youtube-icon classes)
      (instagram-icon classes))))

(defn platform-label
  "The label of the badge; a post that names its own label (a Facebook
   Reel or Story) wins over the platform's default."
  [post]
  (or (:schedule/platform-label post)
      (some-> (social-platform post) :social-platform/name (str " post"))
      (case (post-platform post)
        :reel "Instagram Reel"
        :tiktok "TikTok post"
        :youtube "YouTube Short"
        "Instagram story")))

(defn- badge-text
  "The word next to the icon where the icon alone is ambiguous: an
   Instagram Reel next to the Stories, a Facebook Reel next to the
   Facebook Stories."
  [post]
  (if-let [platform (social-platform post)]
    (:post-type/mark (social-platforms/post-type (:social-platform/id platform)
                                                 (:schedule/post-type post)))
    (case (post-platform post)
      :reel "Reel"
      nil)))

(defn post-date
  [post]
  (:instagram.story.post/date post))

(defn- scheduled-post-query-params
  [post]
  (if (tiktok-post? post)
    {:tiktok-post-eid (:db/id post)}
    {:story-post-eid (:db/id post)}))

(defn edit-link
  [story-post]
  (let [media (:instagram.story.post/media story-post)]
    (cond
      (tiktok-post? story-post)
      (str "/ui/tiktok-publish?story-eid=" (:db/id media))

      (youtube-post? story-post)
      (str "/ui/youtube-publish?story-eid=" (:db/id media))

      (social-platform story-post)
      (str "/ui/social-schedule?story-eid=" (:db/id media))

      (reel-post? story-post)
      (if (:db/id media)
        (str "/ui/reels-publish?"
             (uri/map->query-string {:story-eid (:db/id media)
                                     :reel-eid (:db/id story-post)}))
        ;; A Reel with a directly uploaded video (former Reagent
        ;; Reels screen) has no gallery video to edit:
        (gallery-link/gallery-href))

      (= (:type media) :type.instagram/animation)
      (animation-editor/edit-url (:db/id media))

      (get-in media [:story-media/design-blob :blob/id])
      (str "/ui/editor2?"
           (uri/map->query-string
             (merge {:story-media-eid (:db/id media)
                     :design-blob-id (get-in media [:story-media/design-blob :blob/id])}
                    (scheduled-post-query-params story-post))))

      :else
      (str "/ui/editor2?"
           (uri/map->query-string
             (merge {:editor1-import (:db/id media)}
                    (scheduled-post-query-params story-post)))))))

(defn post-reschedule-link
  [post]
  (let [media-id (get-in post [:instagram.story.post/media :db/id])]
    (str "/ui/social-schedule"
         (when media-id
           (str "?" (uri/map->query-string {:story-eid media-id}))))))

(defn post-cancel-command
  [post]
  (let [command (:story-post/cancel-command post)]
    (if (and (map? command)
             (:command/kind command))
      command
      {:command/kind :command/story-post-cancel
       :command/data {:db/id (:db/id post)}})))

(defn post-preview-url [post]
  (or (:story-media/preview-url post)
      (:story-media/preview-url (:instagram.story.post/media post))))

(defn post-file-name [post]
  (or (:schedule/post-title post)
      (get-in post [:instagram.story.post/media :story-media/name])))

(def ^:private past-statuses
  #{:executed :error :before-24h :back-off :in-progress :canceled})

(defn- past-post?
  [post]
  (contains? past-statuses (:status post)))

(defn cancel-action-visible?
  "Returns true when the agenda card should show the cancel action.
   Retry back-off posts are already in the past, but they are still waiting
   for the next retry attempt and must remain cancelable."
  [post]
  (or (not (past-post? post))
      (= :back-off (:status post))))

(defn platform-error-message
  [post]
  (if-let [platform (social-platform post)]
    (str (:social-platform/name platform) " posting failed. Please try again or contact support.")
    (case (post-platform post)
      :reel "Instagram Reel posting failed. Please try again or contact support."
      :tiktok "TikTok posting failed. Please try again or contact support."
      :youtube "YouTube Short posting failed. Please try again or contact support."
      "Instagram Story posting failed. Please try again or contact support.")))

(defn friendly-error-message
  [message]
  (or (proxy-error/friendly-error-message-short {:message message} nil)
      message))

(defn post-error-message
  [post]
  (or (some-> (:instagram.story.post/error-message-short post)
              (friendly-error-message))
      (some-> (:instagram.story.post/error-message post)
              (friendly-error-message))
      (some-> (:tiktok.post/error-message post)
              (friendly-error-message))
      (some-> (:youtube.short/error-message post)
              (friendly-error-message))
      (:social.post/error-message post)
      (some-> (:reel/error-message-short post)
              (friendly-error-message))
      (some-> (:reel/error-message post)
              (friendly-error-message))
      (when (= :error (:status post))
        (platform-error-message post))))

(defn format-time [date]
  (f/format f/time-formatter date))

(defn format-date [date]
  (f/format f/date-formatter date))

(defn- subscribe-story-strip
  "A row of small story previews on the subscribe screen: what the
   customer already posted and what is scheduled and waiting. `note`
   is an optional warning line under the previews."
  [title posts note]
  (when (seq posts)
    [:div
     {:class ["mb-5" "text-left"]}
     [:p
      {:class ["text-sm" "font-medium" "text-gray-700" "mb-2"]}
      title]
     [:div
      {:class ["flex" "gap-2" "overflow-x-auto"]}
      (map (fn [post]
             [:div
              {:class ["w-16" "sm:w-20" "shrink-0" "relative" "rounded-lg"
                       "overflow-hidden" "bg-gray-900"]
               :style {:aspect-ratio "9/16"}
               :replicant/key (:db/id post)}
              (if-let [url (post-preview-url post)]
                [:img {:src url
                       :class ["w-full" "h-full" "object-cover"]}]
                [:div {:class ["w-full" "h-full"]}])
              [:div
               {:class ["absolute" "bottom-0" "inset-x-0" "px-1" "py-0.5"
                        "bg-gradient-to-t" "from-black/70" "to-transparent"]}
               [:span
                {:class ["text-white" "text-[10px]" "font-medium"]}
                (format-date (post-date post))]]])
           posts)]
     (when note
       [:p
        {:class ["text-xs" "text-amber-600" "mt-2"]}
        note])]))

(defn- subscribe-screen-strips
  "The posted and upcoming story strips for the subscribe screen. Uses
   the agenda posts already loaded for the schedule view (yesterday
   until one month ahead), so both lists can be empty."
  [posts]
  (let [dated (filter post-date posts)
        by-date (fn [ps] (sort-by #(.getTime (post-date %)) ps))
        posted (->> dated
                    (filter #(= :executed (:status %)))
                    (by-date)
                    (take-last 4))
        upcoming (->> dated
                      (remove past-post?)
                      (by-date)
                      (take 4))]
    (list
      (subscribe-story-strip "Stories you already posted" posted nil)
      (subscribe-story-strip "Scheduled and waiting" upcoming
                             "These stories only go live with a topped-up balance."))))

(defn- subscribe-screen [session posts]
  (onboarding-shell
      [:div
       {:class ["text-center" "mb-8"]
        :replicant/key ::subscribe-screen
        ;; Funnel step: the post-first-story conversion moment. Aggregated
        ;; by the admin funnel dashboard as "Subscribe Screen".
        :replicant/on-mount (fn [_]
                              (client-log/log! :onboarding/subscribe-screen))}
       [:img {:src "/img/logo/party_emoji.svg"
              :class ["h-16" "w-16" "mx-auto" "mb-4"]}]
       (step-indicator 3 3)
       [:h1
        {:class ["text-3xl" "sm:text-4xl" "font-bold" "text-gray-900" "mb-3"
                 "tracking-tight"]}
        "You did it!"]
       [:p
        {:class ["text-gray-600" "text-lg" "mb-4"]}
        "Your stories went live on Instagram. You've seen what Storrito can do."]
       [:div
        {:class ["inline-flex" "items-center" "gap-2" "px-4" "py-2" "rounded-full"
                 "bg-green-100" "text-green-700" "text-sm" "font-medium"]}
        [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 24 24"
               :fill "currentColor" :class ["w-4" "h-4"]}
         [:path {:fill-rule "evenodd" :clip-rule "evenodd"
                 :d "M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"}]]
        "Trial stories posted successfully"]]
      [:div
       {:class ["bg-white" "rounded-2xl" "p-6" "sm:p-8" "mb-6"
                "shadow-2xl" "shadow-black/20"]}
       [:p
        {:class ["text-gray-800" "font-semibold" "text-center" "mb-1"]}
        "Ready for unlimited posting?"]
       [:p
        {:class ["text-gray-500" "text-sm" "text-center" "mb-6"]}
        "Keep scheduling stories, manage multiple accounts, and grow your audience."]
       (subscribe-screen-strips posts)
       [:div
        {:class ["mb-6" "space-y-3"]}
        [:div {:class ["flex" "items-baseline" "justify-between" "p-3"
                        "rounded-lg" "bg-green-50"]}
         [:span {:class ["text-sm" "text-green-700" "font-medium"]}
          "Get started from"]
         [:span {:class ["text-lg" "font-bold" "text-green-700"]}
          "$5"]]
        [:p {:class ["text-xs" "text-gray-500" "text-center"]}
         "Pay-as-you-go. Only charged while accounts are connected."]]
       [:div
        {:class ["mb-6" "flex" "justify-evenly" "text-center"
                 "py-3" "border-y" "border-gray-100"]}
        [:div
         [:p {:class ["text-base" "font-semibold" "text-gray-900"]} "366k+"]
         [:p {:class ["text-xs" "text-gray-400"]} "users"]]
        [:div
         [:p {:class ["text-base" "font-semibold" "text-gray-900"]} "14M+"]
         [:p {:class ["text-xs" "text-gray-400"]} "stories posted"]]
        [:div
         [:p {:class ["text-base" "font-semibold" "text-gray-900"]} "152k+"]
         [:p {:class ["text-xs" "text-gray-400"]} "accounts"]]]
       [:a
        ;; The source param attributes the subscription page view to this
        ;; screen in the funnel (see :subscription/page-viewed in
        ;; app.client-log.core).
        {:href "/ui/subscription?source=subscribe-screen"
         :class ["block" "w-full" "py-3.5" "px-6" "rounded-xl" "text-center"
                 "text-white" "font-semibold" "text-base"
                 "bg-storrito-500" "hover:bg-storrito-600"
                 "transition-all" "duration-200"
                 "shadow-lg" "shadow-storrito-500/25"]}
        "Continue with Storrito"]
       (support-link "Questions? Visit our")]
      [:div
       {:class ["text-center" "space-y-3"]}
       [:a {:href "/ui/editor2"
            :class ["text-storrito-500" "hover:text-storrito-600" "text-sm"
                    "underline" "underline-offset-2" "transition-colors"]}
        "Keep exploring"]
       [:div (skip-button ::subscribe-skipped)]]))

(defn group-posts-by-day
  [posts]
  (let [grouping-fn (fn [post]
                      (let [d (-> (post-date post)
                                  tc/from-date
                                  t/to-default-time-zone)]
                        (with-meta
                          ((juxt t/year t/month t/day) d)
                          {:post-date d})))]
    (into (sorted-map)
          (group-by grouping-fn posts))))

;; -- Status badge ------------------------------------------------------------

(defn status-badge [post]
  (case (:status post)
    :executed
    [:span
     {:class ["inline-flex" "items-center" "gap-1" "text-xs" "text-green-700"
              "bg-green-50" "px-2" "py-0.5" "rounded-full" "font-medium"
              "border" "border-green-200"]}
     "Posted"]

    :error
    (let [message (or (post-error-message post)
                      "Something went wrong")]
      [:span
       {:class ["tooltip" "tooltip-bottom" "inline-flex" "items-center" "gap-1"
                "text-xs" "text-red-700" "bg-red-50" "px-2" "py-0.5"
                "rounded-full" "font-medium" "border" "border-red-200"]
        :data-tip message}
       "Failed"])

    :in-progress
    [:span
     {:class ["inline-flex" "items-center" "gap-1" "text-xs" "text-amber-700"
              "bg-amber-50" "px-2" "py-0.5" "rounded-full" "font-medium"
              "border" "border-amber-200"]}
     "Posting..."]

    :back-off
    [:span
     {:class ["tooltip" "tooltip-bottom" "inline-flex" "items-center" "gap-1"
              "text-xs" "text-amber-700" "bg-amber-50" "px-2" "py-0.5"
              "rounded-full" "font-medium" "border" "border-amber-200"]
      :data-tip (str "Will retry at "
                     (f/format-standard-datetime (:instagram.story.post/next-retry post)))}
     "Retrying"]

    :before-24h
    [:span
     {:class ["inline-flex" "items-center" "gap-1" "text-xs" "text-gray-500"
              "bg-gray-50" "px-2" "py-0.5" "rounded-full" "font-medium"
              "border" "border-gray-200"]}
     "Expired"]

    :scheduled
    [:span
     {:class ["inline-flex" "items-center" "gap-1" "text-xs" "text-blue-700"
              "bg-blue-50" "px-2" "py-0.5" "rounded-full" "font-medium"
              "border" "border-blue-200"]}
     "Scheduled"]

    :canceled
    [:span
     {:class ["inline-flex" "items-center" "gap-1" "text-xs" "text-gray-500"
              "bg-gray-50" "px-2" "py-0.5" "rounded-full" "font-medium"
              "border" "border-gray-200"]}
     "Canceled"]

    nil))

;; -- Story card --------------------------------------------------------------

(defn platform-badge
  [post]
  (let [platform (post-platform post)]
    [:span
     {:class ["tooltip" "tooltip-bottom" "inline-flex" "items-center"
              "justify-center" "gap-1" "text-white" "bg-black/60" "px-1.5"
              "py-1" "rounded-full" "font-medium" "pointer-events-auto"]
      :data-tip (platform-label post)
      :aria-label (platform-label post)}
     (platform-icon platform ["w-3" "h-3"])
     (when-let [text (badge-text post)]
       [:span {:class ["text-[10px]" "leading-none"]} text])]))

(defn- cancel-confirm-text
  [post]
  (if-let [platform (social-platform post)]
    (str "Cancel this scheduled " (:social-platform/name platform) " post?")
    (case (post-platform post)
      :reel "Cancel this scheduled Reel?"
      :tiktok "Cancel this scheduled TikTok post?"
      :youtube "Cancel this scheduled YouTube Short?"
      "Cancel this scheduled story?")))

(defn cancel-button
  "The cancel action of the agenda card and the preview modal: asks for
   confirmation, sends the post's cancel command and runs `on-success`.
   `classes` must be a vector: the busy or idle classes are appended."
  [state post classes on-success]
  (let [cancel-command (post-cancel-command post)
        canceling? (command/issued? state cancel-command)
        canceled? (command/success? state cancel-command)]
    [:a
     {:class (into classes
                   (if (or canceling? canceled?)
                     ["opacity-70" "cursor-wait" "pointer-events-none"]
                     ["cursor-pointer"]))
      :aria-disabled (boolean (or canceling? canceled?))
      :on {:click [[:js/confirm (cancel-confirm-text post)]
                   [:data/command
                    cancel-command
                    {:on-success on-success
                     :on-error [[:ui/show-error]]}]]}}
     ;; Canceling can take a few seconds: bundle.social answers in
     ;; up to ~2 s, and the agenda reloads afterwards. "Canceled"
     ;; stays until the reload removes the post.
     (when canceling?
       [:span {:class ["h-3" "w-3" "animate-spin" "rounded-full" "border-2"
                       "border-red-200" "border-t-red-600"]}])
     (cond
       canceling? "Canceling..."
       canceled? "Canceled"
       :else "Cancel")]))

(defn preview-actions
  "Opens `app.agenda.preview-modal` for the post. It lives here as the
   card needs it and the modal requires this namespace."
  [post]
  [[:ui/modal-open :modals/agenda-post-preview
    {:db/id (:db/id post)
     :story-media/eid (get-in post [:instagram.story.post/media :db/id])
     ;; as narrow as its content, like the gallery's entry modal:
     :max-width "sm:max-w-fit"}]])

(defn story-card [state post]
  (let [preview-url (post-preview-url post)
        file-name (post-file-name post)
        status (:status post)
        is-past? (past-post? post)]
    [:div
     {:class ["relative" "flex" "flex-col" "group"]
      :replicant/key (:db/id post)}
     [:div
      {:class ["relative" "rounded-lg" "overflow-hidden" "bg-gray-900"]
       :style {:aspect-ratio "9/16"}}
      (if preview-url
        [:a
         {:href (edit-link post)}
         [:img
          {:src preview-url
           :class (concat
                    ["w-full" "h-full" "object-cover"]
                    (when is-past? ["opacity-60"]))}]]
        [:div
         {:class ["w-full" "h-full" "flex" "items-center" "justify-center"
                  "text-gray-500" "text-sm"]}
         "No preview"])
      [:div
       {:class ["absolute" "bottom-0" "left-0" "right-0" "px-2" "py-1.5"
                "bg-gradient-to-t" "from-black/60" "to-transparent"]}
       [:span
        {:class ["text-white" "text-xs" "font-medium"]}
        (format-time (post-date post))]]
      [:div
       {:class ["absolute" "inset-0" "bg-black/50" "opacity-0"
                "group-hover:opacity-100" "transition-opacity" "duration-200"
                "flex" "flex-col" "items-center" "justify-center" "gap-2"]}
       [:button
        {:type "button"
         :class ["w-4/5" "text-center" "px-3" "py-1.5" "bg-white" "text-gray-900"
                 "text-xs" "font-medium" "rounded-md" "hover:bg-gray-100"
                 "transition-colors" "cursor-pointer"]
         :on {:click (preview-actions post)}}
        "Preview"]
       (when-not (= :executed status)
         [:a
          {:href (edit-link post)
           :class ["w-4/5" "text-center" "px-3" "py-1.5" "bg-white" "text-gray-900"
                   "text-xs" "font-medium" "rounded-md" "hover:bg-gray-100"
                   "transition-colors"]}
          "Edit"])
       [:a
        {:href (post-reschedule-link post)
         :class ["w-4/5" "text-center" "px-3" "py-1.5" "bg-white" "text-gray-900"
                 "text-xs" "font-medium" "rounded-md" "hover:bg-gray-100"
                 "transition-colors"]}
        "Re-schedule"]
       (when (cancel-action-visible? post)
         (cancel-button state
                        post
                        ["w-4/5" "text-center" "px-3" "py-1.5" "bg-white" "text-red-600"
                         "text-xs" "font-medium" "rounded-md" "transition-colors"
                         "inline-flex" "items-center" "justify-center" "gap-1.5"
                         "hover:bg-red-50"]
                        [[:home/refresh]]))]]
     ;; The badge row is a sibling of the preview container (which clips
     ;; its children via overflow-hidden), so the tooltips can extend
     ;; beyond the card bounds without being cut off. The strip itself is
     ;; click-transparent so the hover actions (Edit etc.) underneath stay
     ;; clickable; only the badges re-enable pointer events for their
     ;; tooltips.
     [:div
      {:class ["absolute" "inset-x-1" "top-1" "z-20" "flex" "items-start"
               "justify-between" "gap-1" "pointer-events-none"]}
      [:div
       {:class ["flex" "flex-wrap" "items-center" "gap-1" "min-w-0"]}
       (platform-badge post)
       ;; A blank highlight-id means 'no highlight' — see
       ;; `app.instagram.highlight`.
       (when (not-empty (:instagram.story.post/highlight-id post))
         [:span
          {:data-tip "Saved as a highlight"
           :aria-label "Saved as a highlight"
           :class ["tooltip" "tooltip-bottom" "inline-flex" "items-center"
                   "text-white" "bg-black/60" "px-1.5" "py-1" "rounded-full"
                   "pointer-events-auto"]}
          [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 384 512"
                 :fill "white" :class ["w-3" "h-3"]}
           [:path {:d "M0 48V487.7C0 501.1 10.9 512 24.3 512c5 0 9.9-1.5 14-4.4L192 400 345.7 507.6c4.1 2.9 9 4.4 14 4.4c13.4 0 24.3-10.9 24.3-24.3V48c0-26.5-21.5-48-48-48H48C21.5 0 0 21.5 0 48z"}]]])
       (when (:instagram.story.post.job/share-to-fb? post)
         [:span
          {:data-tip "Cross-post to Facebook"
           :aria-label "Cross-post to Facebook"
           :class ["tooltip" "tooltip-bottom" "inline-flex" "items-center"
                   "text-white" "bg-black/60" "px-1.5" "py-1" "rounded-full"
                   "pointer-events-auto"]}
          [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 512 512"
                 :fill "white" :class ["w-3" "h-3"]}
           [:path {:d "M512 256C512 114.6 397.4 0 256 0S0 114.6 0 256C0 376 82.7 476.8 194.2 504.5V334.2H141.4V256h52.8V222.3c0-87.1 39.4-127.5 125-127.5c16.2 0 44.2 3.2 55.7 6.4V172c-6-.6-16.5-1-29.6-1c-42 0-58.2 15.9-58.2 57.2V256h83.6l-14.4 78.2H287V510.1C413.8 494.8 512 386.9 512 256z"}]]])
       (when (or (:instagram.story.post/ai-generated? post)
                 (:reel/ai-generated? post)
                 (:tiktok.post/ai-generated? post)
                 (:social.post/ai-generated? post))
         [:span
          {:data-tip "Labeled as AI-generated content"
           :aria-label "Labeled as AI-generated content"
           :class ["tooltip" "tooltip-bottom" "inline-flex" "items-center"
                   "text-white" "bg-black/60" "px-1.5" "py-1" "rounded-full"
                   "text-[10px]" "font-bold" "leading-3"
                   "pointer-events-auto"]}
          "AI"])]
      [:span
       {:class ["shrink-0" "pointer-events-auto"]}
       (status-badge post)]]
     [:div
      {:class ["mt-1.5" "px-0.5" "hidden" "sm:block"]}
      [:div
       {:class ["text-xs" "text-gray-700" "font-medium" "truncate"]}
       (or file-name "Untitled")]]]))

;; -- Date navigation --------------------------------------------------------

(defn- set-date-actions
  "Returns actions to set the agenda date and re-query."
  [date]
  [[:agenda/set-date date]])

(defn date-nav
  "Prev/next month navigation with a Today button."
  [state]
  (let [current (agenda-start-date state)
        current-dt (tc/from-date current)
        prev-date (tc/to-date (t/minus current-dt (t/months 1)))
        next-date (tc/to-date (t/plus current-dt (t/months 1)))
        today (tc/to-date (t/today-at-midnight))
        is-today? (= current today)]
    [:div
     {:class ["flex" "items-center" "gap-2"]}
     [:button
      {:class ["px-2" "py-1" "text-gray-500" "hover:text-gray-900"
               "hover:bg-gray-200" "rounded" "transition-colors"]
       :on {:click (set-date-actions prev-date)}}
      "\u2190"]
     [:span
      {:class ["text-sm" "font-medium" "text-gray-700"]}
      (str (format-date current)
           " — "
           (format-date (tc/to-date (t/plus current-dt (t/months 1)))))]
     [:button
      {:class ["px-2" "py-1" "text-gray-500" "hover:text-gray-900"
               "hover:bg-gray-200" "rounded" "transition-colors"]
       :on {:click (set-date-actions next-date)}}
      "\u2192"]
     (when-not is-today?
       [:button
        {:class ["px-2.5" "py-1" "text-xs" "font-medium" "text-indigo-600"
                 "hover:bg-indigo-50" "rounded" "transition-colors"
                 "border" "border-indigo-200"]
         :on {:click (set-date-actions today)}}
        "Today"])]))

;; -- View toggle (link-based) ------------------------------------------------

(defn view-toggle [active-view]
  [:div
   {:class ["flex" "bg-gray-200" "rounded-lg" "p-0.5"]}
   [:a
    {:href "/ui/agenda"
     :class (concat
              ["px-3" "py-1.5" "text-xs" "font-medium" "rounded-md"
               "transition-colors" "no-underline"]
              (if (= active-view :agenda)
                ["bg-white" "text-gray-900" "shadow-sm"]
                ["text-gray-600" "hover:text-gray-900"]))}
    "Agenda"]
   [:a
    {:href "/ui/calendar"
     :class (concat
              ["px-3" "py-1.5" "text-xs" "font-medium" "rounded-md"
               "transition-colors" "no-underline"]
              (if (= active-view :calendar)
                ["bg-white" "text-gray-900" "shadow-sm"]
                ["text-gray-600" "hover:text-gray-900"]))}
    "Calendar"]])

;; -- Empty state -------------------------------------------------------------

(defn empty-schedule [account-connected? org-uuid]
  [:div
   {:class ["text-center" "py-16"]}
   [:div
    {:class ["w-14" "h-14" "mx-auto" "mb-4" "rounded-full" "bg-gray-100"
             "flex" "items-center" "justify-center" "text-2xl"]}
    "\uD83D\uDCC5"]
   [:h3
    {:class ["text-lg" "font-semibold" "text-gray-900" "mb-1"]}
    "No scheduled posts"]
   [:p
    {:class ["text-sm" "text-gray-500" "mb-5" "max-w-sm" "mx-auto"]}
    "Create a story in the editor, then schedule it to post automatically."]
   [:div
    {:class ["flex" "justify-center" "gap-3"]}
    [:a
     {:href "/ui/editor2"
      :class ["inline-flex" "items-center" "gap-1.5" "px-4" "py-2"
              "text-sm" "font-medium" "text-white" "bg-indigo-600"
              "hover:bg-indigo-700" "rounded-lg" "transition-colors"]}
     "Create Story"]
    [:a
     {:href (gallery-link/gallery-href)
      :class ["inline-flex" "items-center" "gap-1.5" "px-4" "py-2"
              "text-sm" "font-medium" "text-gray-700" "bg-white" "border"
              "border-gray-300" "hover:bg-gray-50" "rounded-lg" "transition-colors"]}
     "Open Gallery"]]
   [:div
    {:class ["mt-4"]}
    (if account-connected?
      [:a
       {:href (calendly/url :demo-call "empty-state" org-uuid)
        :target "_blank"
        :class ["text-sm" "font-medium" "text-blue-600" "hover:underline"]}
       "Need Help Posting Your First Story?"]
      [:a
       {:href (calendly/url :connect-help "empty-state" org-uuid)
        :target "_blank"
        :class ["text-sm" "font-medium" "text-blue-600" "hover:underline"]}
       "Need Help Getting Started?"])]])

;; -- Schedule shell ----------------------------------------------------------

(defn schedule-shell
  "Wraps a schedule view (agenda or calendar) with the shared header,
   account selector, and onboarding checks.
   Accepts optional opts map with :secondary-sidebar-fn (fn [state] -> hiccup)."
  [state active-view content-fn & [{:keys [secondary-sidebar-fn]}]]
  (let [q (home-query state)
        home-result (query/get-result state q)
        session (query/get-result state user-session-query)
        users (:instagram/users home-result)
        accounts (or (:schedule/accounts home-result)
                     users)
        selected-account-key (or (::selected-account-key state)
                                 (:schedule.account/selected-key home-result)
                                 (some-> (:instagram.user/selected-id home-result)
                                         instagram-account-key))
        selected-account (some #(when (= selected-account-key (:account/key %)) %)
                               accounts)]
    (cond
      (and (or (query/loading? state q)
              (query/loading? state user-session-query))
           (not home-result))
      [:div
       {:class ["flex" "items-center" "justify-center" "min-h-screen"]}
       [:div {:class ["text-gray-500"]} "Loading..."]]

      (and (empty? accounts)
           (not (::connect-skipped state)))
      (onboarding-frame state
                        (welcome-connect-screen session))

      (not (:organization/has-content? home-result))
      (onboarding-frame state
                        (create-screen session accounts
                                       (when (and (navbar/trial-user? session)
                                                  (connected-account? home-result))
                                         (:trial/days-left home-result))))

      (show-subscribe-screen? session (::subscribe-skipped state))
      (onboarding-frame state
                        (subscribe-screen session (:agenda/posts home-result)))

      :else
      (app-frame/app-frame
        state
        [:div
         {:class ["space-y-3" "sm:space-y-4"]}
         [:div
          {:class ["flex" "items-center" "gap-2" "sm:gap-3"]}
          (if (seq accounts)
            [:div
             {:class ["min-w-0" "flex-1"]}
             (account-selector/render
               {:state state
                :users accounts
                :selected-id selected-account-key
                :click-actions-fn (fn [account-key]
                                    [[:store/assoc ::selected-account-key account-key]
                                     [:schedule/save-selection account-key]
                                     [:home/refresh]])
                :add-action add-social-account-action
                :add-title "Add social account"
                :add-label "Add social account"})]
            [:div {:class ["flex-grow"]}])
          [:button
           {:class ["inline-flex" "items-center" "gap-1.5" "p-2" "sm:px-4" "sm:py-2"
                    "text-sm" "font-medium" "text-gray-700" "bg-white"
                    "border" "border-gray-300" "hover:bg-gray-50"
                    "rounded-lg" "transition-colors" "cursor-pointer"
                    "shadow-sm" "shrink-0"]
            :title "Upload"
            :on {:click [[:upload/pick-files]]}}
           [:svg {:xmlns "http://www.w3.org/2000/svg" :viewBox "0 0 20 20"
                  :fill "currentColor" :class ["w-4" "h-4"]}
            [:path {:d "M9.25 13.25a.75.75 0 001.5 0V4.636l2.955 3.129a.75.75 0 001.09-1.03l-4.25-4.5a.75.75 0 00-1.09 0l-4.25 4.5a.75.75 0 101.09 1.03L9.25 4.636v8.614z"}]
            [:path {:d "M3.5 12.75a.75.75 0 00-1.5 0v2.5A2.75 2.75 0 004.75 18h10.5A2.75 2.75 0 0018 15.25v-2.5a.75.75 0 00-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5z"}]]
           [:span {:class ["hidden" "sm:inline"]} "Upload"]]
          [:a
           {:href "/ui/editor2"
            :class ["inline-flex" "items-center" "gap-1.5" "px-4" "py-2"
                    "text-sm" "font-medium" "text-white" "bg-gray-900"
                    "hover:bg-gray-800" "rounded-lg" "transition-colors"
                    "shadow-sm" "shrink-0" "hidden" "sm:inline-flex"]}
           "+ New Story"]]
         (when (show-trial-banner? session
                                   home-result
                                   (::trial-banner-dismissed state))
           (trial-banner session (:trial/days-left home-result)))
         (account-selector/reconnect-banner selected-account)
         (content-fn state home-result)]
        (when secondary-sidebar-fn
          {:secondary-sidebar (secondary-sidebar-fn state)})))))

(def on-load-actions
  [[:ui/hide-sidebar-on-mobile]
   [:schedule/restore-selection]
   [:home/refresh]
   [:data/query {:query/kind :query/user-session}]])
